/**
 * AssetManagerPanel — Convex-backed asset list, upload, delete.
 */
import { useRef, useState } from 'react';
import { useQuery, useMutation, useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { uploadAsset } from '@/lib/uploads';
import { useCanvasStore } from '@/store/canvasStore';
import { useAppStore } from '@/store/appStore';
import { Image, Trash2, Upload, Plus, Scan } from 'lucide-react';
import { AssetImage } from './AssetImage';
import { OCRImportModal } from './OCRImportModal';
import { useConfirm } from '@/hooks/useConfirm';

export function AssetManagerPanel() {
  const { confirm, dialog } = useConfirm();
  const convex = useConvex();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showOcr, setShowOcr] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [uploadType, setUploadType] = useState<string>('logo');
  const { addLayer } = useCanvasStore();
  const { setActivePanel } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;

  const assets = useQuery(api.assets.list, { type: typeFilter, accountId });
  const removeAsset = useMutation(api.assets.remove);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await uploadAsset(convex, {
          file,
          type: uploadType,
          name: file.name,
          accountId,
        });
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (id: Id<'assets'>) => {
    const asset = assets?.find((a) => a._id === id);
    const confirmed = await confirm({
      title: 'Delete Asset',
      message: `Are you sure you want to delete "${asset?.name || 'this asset'}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await removeAsset({ accountId, id });
    } catch (err) {
      console.error('Remove failed:', err);
    }
  };

  const handleAddToCanvas = (a: { _id: Id<'assets'>; name: string }) => {
    addLayer({
      type: 'image',
      name: a.name,
      visible: true,
      locked: false,
      x: 100,
      y: 100,
      width: 200,
      height: 200,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      asset_id: a._id,
    });
    setActivePanel('canvas');
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <div className="flex-shrink-0 border-b border-border p-4 space-y-3 bg-card/30">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value)}
            className="rounded-lg border border-input bg-background px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
          >
            <option value="logo">Logo</option>
            <option value="photo">Photo</option>
            <option value="background">Background</option>
            <option value="texture">Texture</option>
            <option value="flyer_reference">Flyer reference</option>
            <option value="generated">Generated</option>
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-[0.98] hover:shadow-sm hover:shadow-primary/20"
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
          <button
            onClick={() => setShowOcr(true)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted/50 hover:border-muted-foreground/20 transition-all"
          >
            <Scan className="h-4 w-4" />
            OCR
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setTypeFilter(undefined)}
            className={`rounded-full px-2.5 py-1 text-xs font-medium transition-all ${
              !typeFilter ? 'bg-primary/15 text-primary shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            All {assets ? `(${assets.length})` : ''}
          </button>
          {['logo', 'photo', 'background', 'generated', 'texture', 'flyer_reference'].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(typeFilter === t ? undefined : t)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize transition-all ${
                typeFilter === t ? 'bg-primary/15 text-primary shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {assets === undefined ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center animate-fade-in">
            <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center">
              <Image className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <div>
              <p className="font-medium text-foreground/80">No assets yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Upload logos, photos, or backgrounds
              </p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 flex items-center gap-1.5 rounded-lg bg-primary/10 text-primary px-4 py-2 text-sm font-medium hover:bg-primary/20 transition-colors"
            >
              <Upload className="h-4 w-4" />
              Upload first asset
            </button>
          </div>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {assets.map((a) => (
              <li
                key={a._id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/asset-id', a._id);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className="group relative rounded-xl border border-border bg-card overflow-hidden cursor-grab active:cursor-grabbing transition-all duration-150 hover:border-primary/30 hover:shadow-md hover:shadow-primary/5 hover:-translate-y-0.5"
              >
                <div className="aspect-square bg-muted/50 flex items-center justify-center overflow-hidden relative">
                  <AssetImage id={a._id} alt={a.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToCanvas(a); }}
                    className="absolute bottom-2 right-2 rounded-lg p-2 bg-primary text-primary-foreground opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-primary/90 shadow-lg"
                    title="Add to canvas"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-2.5">
                  <div className="font-medium text-sm truncate">{a.name}</div>
                  <div className="text-[11px] text-muted-foreground capitalize mt-0.5">{(a.type || '').replace('_', ' ')}</div>
                </div>
                <button
                  onClick={() => handleRemove(a._id)}
                  className="absolute top-2 right-2 rounded-lg p-1.5 bg-background/80 backdrop-blur-sm text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <OCRImportModal open={showOcr} onClose={() => setShowOcr(false)} />
      {dialog}
    </div>
  );
}
